import { MouseEvent, memo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
} from '@mui/material';

import { useIsViewer } from '../auth/hooks/useIsViewer';
import { Analyzer, Snapshot } from 'types';
import { useIsAnalyzerInCrosspolMode } from 'features/analyzers/hooks/useIsAnalyzerInCrosspolMode';
import { useIsSnapshotLocked } from '../analyzers/hooks/useIsSnapshotLocked';

import { startOfDay } from 'date-fns';
import { useIsBaseEnabled } from 'features/analyzerModules/api/getAnalyzerModules';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';

interface Props {
  analyzer: Analyzer | undefined;
  snapshot: Snapshot | undefined;
  openMonitorDialog: () => void;
  openThresholdDialog: () => void;
  openMaskDialog: () => void;
  handleDrawInterferenceDetector: (detectorType: InterferenceDetector) => void;
  handleBaselineClicked: (event: MouseEvent<HTMLLIElement>) => void;
  handleMaxClicked: (event: MouseEvent<HTMLLIElement>) => void;
  handleMinClicked: (event: MouseEvent<HTMLLIElement>) => void;
  handleMinMaxResetClicked: () => void;
  handleClearClicked: (detectorType: InterferenceDetector) => void;
  handleCrossPolClicked: () => Promise<void>;
  inDialog?: boolean;
  handleDialogClose?: () => void;
}

const SingleTraceViewToolbar = memo(
  ({
    analyzer,
    snapshot,
    openMonitorDialog,
    openThresholdDialog,
    openMaskDialog,
    handleDrawInterferenceDetector,
    handleBaselineClicked,
    handleMaxClicked,
    handleMinClicked,
    handleMinMaxResetClicked,
    handleClearClicked,
    handleCrossPolClicked,
    inDialog,
    handleDialogClose,
  }: Props) => {
    const params = useParams();
    const wallId = Number(params.wallId);
    const isViewer = useIsViewer();
    const isSnapshotLocked = useIsSnapshotLocked(snapshot);
    const navigate = useNavigate();
    const { timezone, browserTimezone } = useUserDateTime();

    const [monitorAnchorEl, setMonitorAnchorEl] = useState<null | Element>(
      null
    );
    const [maskAnchorEl, setMaskAnchorEl] = useState<null | Element>(null);
    const [traceAnchorEl, setTraceAnchorEl] = useState<null | Element>(null);
    const [historyAnchorEl, setHistoryAnchorEl] = useState<null | Element>(
      null
    );

    const baseEnabled = useIsBaseEnabled(analyzer?.id);

    const crossPolEnabled = useIsAnalyzerInCrosspolMode(analyzer?.id || -1);

    const handleMonitorMenuClicked = (event: React.MouseEvent) => {
      setMonitorAnchorEl(event.currentTarget);
    };

    const handleMonitorMenuClose = () => {
      setMonitorAnchorEl(null);
    };
    const handleTraceMenuClicked = (event: React.MouseEvent) => {
      setTraceAnchorEl(event.currentTarget);
    };

    const handleTraceMenuClose = () => {
      setTraceAnchorEl(null);
    };

    const handleMaskMenuClicked = (event: React.MouseEvent) => {
      setMaskAnchorEl(event.currentTarget);
    };

    const handleMaskMenuClose = () => {
      setMaskAnchorEl(null);
    };

    const handleHistoryMenuClicked = (event: React.MouseEvent) => {
      setHistoryAnchorEl(event.currentTarget);
    };

    const handleCloseHistoryMenu = () => {
      setHistoryAnchorEl(null);
    };

    const handleMonitorSettingsClicked = () => {
      openMonitorDialog();
      handleMonitorMenuClose();
    };

    const handleThresholdSettingsClicked = () => {
      openThresholdDialog();
      handleMonitorMenuClose();
    };

    const handleMaskSettingsClicked = () => {
      openMaskDialog();
      handleMaskMenuClose();
    };

    const handleJumpToTraceHistory = () => {
      if (!snapshot) return;
      if (inDialog && handleDialogClose) handleDialogClose();

      const currentDateTime = new Date();
      const startOfDayString = zonedTimeToUtc(
        startOfDay(currentDateTime),
        timezone
      ).toISOString();
      const currentDateTimeString = zonedTimeToUtc(
        currentDateTime,
        browserTimezone
      ).toISOString();

      navigate(
        `/trace-history?start=${startOfDayString}&end=${currentDateTimeString}&analyzer=${analyzer?.id}&snapshot=${snapshot?.id}&trace=1`
      );
    };

    const handleJumpToMarkerHistory = () => {
      if (!snapshot) return;
      if (inDialog && handleDialogClose) handleDialogClose();

      navigate(
        `/marker-history?analyzerId=${analyzer?.id}&snapshotId=${snapshot?.id}`
      );
    };

    const handleDetailedViewClicked = () => {
      if (!snapshot) return;

      navigate(`/detailed-view/${wallId}/${analyzer?.id}/${snapshot?.id}`);
    };

    return (
      <Grid container item>
        <Grid item xs={1} style={{ minWidth: '170px' }}>
          <Button
            onClick={handleTraceMenuClicked}
            size='large'
            fullWidth
            disabled={
              isViewer ||
              !snapshot ||
              !!snapshot.crosspol_snapshot_id ||
              isSnapshotLocked
            }
          >
            Trace
          </Button>
          <Menu
            id='trace-menu'
            anchorEl={traceAnchorEl}
            open={Boolean(traceAnchorEl)}
            onClose={handleTraceMenuClose}
          >
            <MenuItem
              onClick={(event) => {
                handleTraceMenuClose();
                handleBaselineClicked(event);
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!snapshot?.baseline_enabled}
                    name='baseline-checkbox'
                  />
                }
                label='Baseline'
              />
            </MenuItem>
            <MenuItem
              onClick={(event) => {
                handleTraceMenuClose();
                handleMinClicked(event);
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      snapshot?.minmax_enabled === 1 ||
                      snapshot?.minmax_enabled === 3
                    }
                    name='min-checkbox'
                  />
                }
                label='Min'
              />
            </MenuItem>
            <MenuItem
              onClick={(event) => {
                handleTraceMenuClose();
                handleMaxClicked(event);
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      snapshot?.minmax_enabled === 2 ||
                      snapshot?.minmax_enabled === 3
                    }
                    name='max-checkbox'
                  />
                }
                label='Max'
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleTraceMenuClose();
                handleMinMaxResetClicked();
              }}
            >
              Reset Min Max
            </MenuItem>
          </Menu>
        </Grid>
        {baseEnabled && !isViewer && (
          <Grid item xs={1} style={{ minWidth: '170px' }}>
            <Button
              onClick={handleMonitorMenuClicked}
              size='large'
              fullWidth
              disabled={
                !snapshot || !!snapshot.crosspol_snapshot_id || isSnapshotLocked
              }
            >
              Monitors
            </Button>

            <Menu
              id='monitors-menu'
              anchorEl={monitorAnchorEl}
              open={Boolean(monitorAnchorEl)}
              onClose={handleMonitorMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleMonitorMenuClose();
                  handleDrawInterferenceDetector(InterferenceDetector.Monitor);
                }}
              >
                Create Monitors
              </MenuItem>
              <MenuItem onClick={handleMonitorSettingsClicked}>
                Monitor Settings
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClearClicked(InterferenceDetector.Monitor);
                  handleMonitorMenuClose();
                }}
              >
                Clear Monitors
              </MenuItem>
              <Divider
                sx={{ bgcolor: 'gray' }}
                style={{ border: 'none', height: 1, margin: 0 }}
              />
              <MenuItem
                onClick={() => {
                  handleMonitorMenuClose();
                  handleDrawInterferenceDetector(
                    InterferenceDetector.Threshold
                  );
                }}
              >
                Create Threshold
              </MenuItem>
              <MenuItem onClick={handleThresholdSettingsClicked}>
                Threshold Settings
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClearClicked(InterferenceDetector.Threshold);
                  handleMonitorMenuClose();
                }}
              >
                Clear Thresholds
              </MenuItem>
            </Menu>
          </Grid>
        )}
        {baseEnabled && !isViewer && (
          <Grid item xs={1} style={{ minWidth: '170px' }}>
            <Button
              onClick={handleMaskMenuClicked}
              size='large'
              fullWidth
              disabled={
                !snapshot || !!snapshot.crosspol_snapshot_id || isSnapshotLocked
              }
            >
              Spectral Masks
            </Button>
            <Menu
              id='spectral-masks-menu'
              anchorEl={maskAnchorEl}
              open={Boolean(maskAnchorEl)}
              onClose={handleMaskMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleMaskMenuClose();
                  handleDrawInterferenceDetector(InterferenceDetector.Mask);
                }}
              >
                Create Spectral Mask
              </MenuItem>
              <MenuItem onClick={handleMaskSettingsClicked}>
                Spectral Mask Settings
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClearClicked(InterferenceDetector.Mask);
                  handleMaskMenuClose();
                }}
              >
                Clear Spectral Masks
              </MenuItem>
            </Menu>
          </Grid>
        )}
        {!isViewer && (
          <Grid item xs={1} style={{ minWidth: '170px' }}>
            <Button
              onClick={handleCrossPolClicked}
              size='large'
              fullWidth
              disabled={
                !snapshot ||
                analyzer?.in_time_share ||
                (!baseEnabled &&
                  crossPolEnabled &&
                  !snapshot.crosspol_snapshot_id) ||
                isSnapshotLocked
              }
            >
              {snapshot?.crosspol_snapshot_id
                ? 'Stop Cross-Pol'
                : 'Start Cross-Pol'}
            </Button>
          </Grid>
        )}
        {baseEnabled && !isViewer && (
          <Grid item xs={1} style={{ minWidth: '170px' }}>
            <Button onClick={handleHistoryMenuClicked} size='large' fullWidth>
              History
            </Button>
            <Menu
              id='history-menu'
              anchorEl={historyAnchorEl}
              open={Boolean(historyAnchorEl)}
              onClose={handleCloseHistoryMenu}
            >
              <MenuItem onClick={handleJumpToTraceHistory}>
                Trace History
              </MenuItem>
              <MenuItem onClick={handleJumpToMarkerHistory}>
                Marker History
              </MenuItem>
            </Menu>
          </Grid>
        )}
        {baseEnabled && !inDialog && (
          <Grid item xs={1} style={{ minWidth: '170px' }}>
            <Button onClick={handleDetailedViewClicked} size='large' fullWidth>
              Detailed View
            </Button>
          </Grid>
        )}
      </Grid>
    );
  }
);

export default SingleTraceViewToolbar;
